import { useState, useEffect } from "react"
import {
  SubmitBtn,
  ActionSection,
  SideCancelBtn,
} from "../Styles/InputStyles"
import { MdClose } from "react-icons/md"
import tw from "tailwind-styled-components"
import "react-dropdown/style.css"
import { GoSearch } from "react-icons/go"
import SelectCategoryModal from "./SelectCategoryModal"
import { toast } from "react-toastify"

const FilterModal = ({
  categories,
  onApplyFilter,
  handleClose,
  handleClear,
  selectedCategory,
  setSelectedCategory,
  selectedSortBy,
  setSelectedSortBy
}) => {
  const [height, setHeight] = useState(window.innerHeight);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [activeFilterType, setActiveFilterType] = useState("category");

  const handleSelectedCategory = (selectedOption) => {
    setSelectedCategory(selectedOption)
  }

  const handleCategorySave = (category) => {
    handleSelectedCategory(category)
    setShowCategoryModal(false)
  }

  const handleSortByChange = (option) => {
    setSelectedSortBy(option);
  }

  const handleApplyFilter = () => {
    if (selectedCategory || selectedSortBy) {
      onApplyFilter(
        selectedCategory?.value || "",
        selectedCategory?.label || "",
        selectedSortBy?.value || ""
      );
    }
    else {
      toast.error("Please select at least one filter before applying.");
    }
  };

  const handleClearFilters = () => {
    setSelectedCategory(null);
    setSelectedSortBy(null);
    handleClear();
  };

  useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight)
    }
    window.addEventListener("resize", handleResize)
    handleResize()
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  useEffect(() => {
    if (selectedCategory) {
      setActiveFilterType("category");
    } else if (selectedSortBy) {
      setActiveFilterType("sortBy");
    } else {
      setActiveFilterType("category");
    }
  }, [selectedCategory, selectedSortBy]);

  const filterGroups = [
    {
      key: "category",
      title: "Category",
    },
    {
      key: "sortBy",
      title: "Sort By",
    },
  ];

  const sortByOptions = [
    { label: "Paid Date", value: "PD" },
    { label: "Due Date", value: "DD" },
  ];

  return (
    <Container style={{ height: `${height}px`, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <Header>
        <h1 className="text-lg font-medium text-white">Filters</h1>
        <button onClick={handleClose}>
          <MdClose color="white" size={24} />
        </button>
      </Header>

      <Body>
        <LeftPane>
          {filterGroups.map((group) => (
            <TypeItem
              key={group.key}
              active={activeFilterType === group.key}
              onClick={() => setActiveFilterType(group.key)}
            >
              {group.title}
            </TypeItem>
          ))}
        </LeftPane>

        <RightPane>
          {activeFilterType === "category" && (
            <>
              <FilterTitle>Search By Category</FilterTitle>

              <div className="relative mt-2">
                <input
                  type="text"
                  placeholder="Search categories..."
                  onClick={(e) => setShowCategoryModal(true)}
                  className="w-full px-8 h-12 py-2 border border-gray-200 rounded-md focus:outline-none focus:border-primary-color text-gray-800 placeholder-gray-300 cursor-pointer"
                  readOnly
                  value={selectedCategory ? selectedCategory.label : ""}
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <GoSearch className="w-4 h-4 text-gray-400" />
                </div>
              </div>

              {selectedCategory && (
                <div className="flex flex-wrap gap-2 mt-4">
                  <div className="flex items-center bg-primary-color/10 text-primary-color 
                            px-3 py-1 rounded-full text-sm">
                    {selectedCategory.label}
                    <button
                      onClick={() => setSelectedCategory(null)}
                      className="ml-2 text-primary-color hover:text-red-500"
                    >
                      <MdClose size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {activeFilterType === "sortBy" && (
            <>
              <FilterTitle>Sort By</FilterTitle>

              {sortByOptions.map((option) => (
                <OptionRow
                  key={option.value}
                  onClick={() => handleSortByChange(option)}
                  active={selectedSortBy?.value === option.value}
                >
                  <input
                    type="radio"
                    name="sortBy"
                    checked={selectedSortBy?.value === option.value}
                    onChange={() => handleSortByChange(option)}
                    className="w-4 h-4 text-primary-color focus:ring-primary-color"
                  />
                  <span className="flex items-center gap-2 text-sm text-gray-600">
                    {option.label}
                  </span>
                </OptionRow>
              ))}
            </>
          )}
        </RightPane>
      </Body>

      <ActionSection>
        <SideCancelBtn type="button" onClick={handleClearFilters}>
          Clear
        </SideCancelBtn>
        <SubmitBtn type="submit" onClick={handleApplyFilter}>
          Apply Filter
        </SubmitBtn>
      </ActionSection>

      <SelectCategoryModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        onSelect={handleCategorySave}
        categories={categories}
        selectedCategory={selectedCategory}
      />
    </Container>
  )
}

const Container = tw.div`bg-white w-full h-full flex flex-col`;
const Header = tw.div`flex items-center justify-between bg-primary-color px-4 py-4`;

const Body = tw.div`flex flex-1 overflow-hidden`;

const LeftPane = tw.div`
  w-1/3 bg-gray-50 border-r overflow-y-auto`;

const TypeItem = tw.div`
  px-4 py-4 cursor-pointer text-md relative
  ${({ active }) =>
    active
      ? "bg-blue-50 text-primary-color font-medium border-l-4 border-primary-color"
      : "text-gray-700 hover:bg-gray-100"}
`;

const RightPane = tw.div`
  flex-1
  py-4
  pl-4
  pr-6
  overflow-y-auto
`;

const OptionRow = tw.div`
  flex items-center gap-3
  py-3 px-2
  cursor-pointer
  text-sm text-gray-400
  rounded-md
  hover:bg-gray-50
  ${({ active }) => active ? "bg-blue-50" : ""}
`;

const FilterTitle = tw.h3`text-md font-medium text-gray-700 mb-2`;

export default FilterModal;